import React, { createContext, useContext, useState, useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export interface MenuContextType {
  isMenuOpen: boolean;
  isMiniMode: boolean;
  isMobileMenuOpen: boolean;
  isDesktop: boolean;
  isMiniSwitchVisibility: boolean;
  isAuthMenuOpen: boolean;
  toggleThis: (key: string, value?: boolean) => void;
  setMenuValue: (key: string, value: boolean) => void;
}

export const MenuContext = createContext<MenuContextType>({
  isMenuOpen: true,
  isMiniMode: false,
  isMobileMenuOpen: false,
  isDesktop: true,
  isMiniSwitchVisibility: true,
  isAuthMenuOpen: false,
  toggleThis: () => {},
  setMenuValue: () => {},
});

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [menuState, setMenuState] = useState({
    isMenuOpen: true,
    isMiniMode: false,
    isMobileMenuOpen: false,
    isMiniSwitchVisibility: true,
    isAuthMenuOpen: false,
  });

  useEffect(() => {
    if (!isDesktop) {
      setMenuState((prev) => ({ ...prev, isMenuOpen: false }));
    } else {
      setMenuState((prev) => ({ ...prev, isMenuOpen: true }));
    }
  }, [isDesktop]);

  const toggleThis = (key: string, value?: boolean) => {
    setMenuState((prev: any) => ({
      ...prev,
      [key]: value !== undefined ? value : !prev[key],
    }));
  };

  const setMenuValue = (key: string, value: boolean) => {
    setMenuState((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <MenuContext.Provider
      value={{
        ...menuState,
        isDesktop,
        toggleThis,
        setMenuValue,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => useContext(MenuContext);
export default MenuContext;
